import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Grid, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import ReplayIcon from '@mui/icons-material/Replay';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Permission,
  ValidationSeverity,
  type AnalysisIssue,
  type MessageEvent,
  type SimulationRun,
} from '@k-ucap/shared';
import {
  AnalysisSummary,
  MessageEventBrowser,
  AnalysisTree,
  AnalysisDetailPanel,
} from '@/components/analyzer/index.js';
import { useAnalyzer } from '@/hooks/useAnalyzer.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useDeepLinkContext, buildDeepLink } from '@/hooks/useDeepLinkContext.js';
import { Disclaimer } from '@/components/common/Disclaimer.js';

export const AnalyzerPage = () => {
  const navigate = useNavigate();
  const analyzer = useAnalyzer();
  const deepLink = useDeepLinkContext();
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const hasAnalyzePermission = useAuthStore((s) => s.hasPermission(Permission.MESSAGE_ANALYZE));
  const [selectedIssue, setSelectedIssue] = useState<AnalysisIssue | null>(null);
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const { simulationRunRepository } = useRepositories();

  // Prompt 14 Phase 3: error navigation
  const errorEvents = useMemo(
    () =>
      analyzer.filteredEvents.filter(
        (e) => e.validation === ValidationSeverity.FAIL || e.validation === ValidationSeverity.WARN
      ),
    [analyzer.filteredEvents]
  );

  const navigateError = (direction: 'prev' | 'next') => {
    if (errorEvents.length === 0) return;
    const currentIndex = analyzer.selectedEvent
      ? errorEvents.findIndex((e) => e.id === analyzer.selectedEvent?.id)
      : -1;
    const nextIndex =
      direction === 'next'
        ? currentIndex < 0
          ? 0
          : (currentIndex + 1) % errorEvents.length
        : currentIndex < 0
          ? errorEvents.length - 1
          : (currentIndex - 1 + errorEvents.length) % errorEvents.length;
    analyzer.selectEvent(errorEvents[nextIndex]);
  };

  useEffect(() => {
    if (!selectedProject) {
      setRuns([]);
      return;
    }
    simulationRunRepository
      .findByProjectId(selectedProject.id)
      .then((loaded) => setRuns(loaded))
      .catch(() => setRuns([]));
  }, [selectedProject, simulationRunRepository]);

  const handleSelectEvent = (event: MessageEvent) => {
    setSelectedIssue(null);
    analyzer.selectEvent(event);
  };

  const handleSelectIssue = (issue: AnalysisIssue) => {
    setSelectedIssue(issue);
  };

  if (!hasAnalyzePermission) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          메시지 분석기
        </Typography>
        <Alert severity="error">메시지 분석기 접근 권한(MESSAGE_ANALYZE)이 없습니다.</Alert>
        <Disclaimer />
      </Box>
    );
  }

  return (
    <Box>
      <AnalysisSummary
        result={analyzer.analysisResult}
        autoAnalyze={analyzer.autoAnalyze}
        autoFollow={analyzer.autoFollow}
        onToggleAutoAnalyze={() => analyzer.setAutoAnalyze((v) => !v)}
        onToggleAutoFollow={() => analyzer.setAutoFollow((v) => !v)}
        totalEvents={analyzer.events.length}
        passCount={analyzer.statusCounts.pass}
        warnCount={analyzer.statusCounts.warn}
        failCount={analyzer.statusCounts.fail}
        unknownCount={analyzer.statusCounts.unknown}
        unsupportedCount={analyzer.statusCounts.unsupported}
      />

      {!selectedProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          프로젝트를 선택해야 메시지 이벤트를 조회할 수 있습니다. Settings에서 프로젝트를 선택하세요.
        </Alert>
      )}

      {analyzer.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {analyzer.error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>실행(Run) 필터</InputLabel>
          <Select
            value={analyzer.filter.runId}
            label="실행(Run) 필터"
            onChange={(e) => analyzer.updateFilter({ runId: e.target.value as string })}
          >
            <MenuItem value="">전체</MenuItem>
            {runs.map((run) => (
              <MenuItem key={run.id} value={run.id}>
                {run.name} ({new Date(run.startedAt).toLocaleString()})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} sx={{ height: 'calc(100vh - 360px)', minHeight: 480 }}>
        <Grid size={{ xs: 12, md: 3 }} sx={{ height: '100%' }}>
          <MessageEventBrowser
            events={analyzer.filteredEvents}
            selectedEvent={analyzer.selectedEvent}
            onSelect={handleSelectEvent}
            messageTypes={analyzer.messageTypes}
            sourceRoles={analyzer.sourceRoles as string[]}
            targetRoles={analyzer.targetRoles as string[]}
            directions={analyzer.directions}
            filter={analyzer.filter}
            onFilterChange={analyzer.updateFilter}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }} sx={{ height: '100%' }}>
          <AnalysisTree
            result={analyzer.analysisResult}
            selectedIssue={selectedIssue}
            onSelectIssue={handleSelectIssue}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%' }}>
          <AnalysisDetailPanel
            event={analyzer.selectedEvent}
            result={analyzer.analysisResult}
            selectedIssue={selectedIssue}
            events={analyzer.events}
            onSelectEvent={handleSelectEvent}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<NavigateBeforeIcon />}
          onClick={() => navigateError('prev')}
          disabled={errorEvents.length === 0}
        >
          Prev Error
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<NavigateNextIcon />}
          onClick={() => navigateError('next')}
          disabled={errorEvents.length === 0}
        >
          Next Error
        </Button>
        {analyzer.selectedEvent?.runId && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplayIcon />}
            onClick={() =>
              navigate(
                buildDeepLink('/scenario', {
                  sessionId: deepLink.sessionId,
                  runId: analyzer.selectedEvent?.runId ?? null,
                  messageId: analyzer.selectedEvent?.id ?? null,
                })
              )
            }
          >
            Replay
          </Button>
        )}
        <Button variant="outlined" onClick={analyzer.clearSelection} disabled={!analyzer.selectedEvent}>
          Clear Selection
        </Button>
        <Button
          variant="outlined"
          onClick={analyzer.runAnalysis}
          disabled={!analyzer.selectedEvent}
          data-testid="analyzer-run-button"
        >
          Run Analysis
        </Button>
        {analyzer.selectedEvent && [101, 104, 110, 1100].includes(analyzer.selectedEvent.messageType) && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<MapIcon />}
            onClick={() => {
              const vehicleId = analyzer.selectedEvent?.fields?.vehicleId;
              navigate(
                buildDeepLink('/gis', {
                  sessionId: deepLink.sessionId,
                  vehicleId: vehicleId && typeof vehicleId === 'string' ? vehicleId : null,
                  timestamp: analyzer.selectedEvent?.timestamp ?? null,
                })
              );
            }}
          >
            GIS에서 보기
          </Button>
        )}
      </Box>

      <Disclaimer />
    </Box>
  );
};
